import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, CalendarCheck, Building2, Users, BarChart3, Settings,
  ListChecks, X, ShieldAlert, CreditCard, Star, CalendarDays, BedDouble, UserCheck,
  Gavel, ScanLine, QrCode, LifeBuoy, RotateCcw, Ticket, Banknote, HandCoins,
  Wallet,
} from "lucide-react";
import { useAuth, ROLE_LABEL, type Permission, type AdminRole } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import favicon from "@/assets/favicon.webp";

const BUSINESS_OWNER_ROLES: AdminRole[] = [
  "venue_manager", "hotel_owner", "event_centre_owner",
  "open_space_owner", "resort_owner", "hotel_event_owner",
  "apartment_owner", "conference_hall_owner",
];

type Item = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; perm?: Permission; managerOnly?: boolean; adminOnly?: boolean; modOnly?: boolean };

const PRIMARY: Item[] = [
  { to: "/admin/dashboard",  label: "Dashboard",   icon: LayoutDashboard },
  { to: "/admin/analytics",  label: "Analytics",   icon: BarChart3,      adminOnly: true },
  { to: "/admin/manager-analytics", label: "Analytics", icon: BarChart3, perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/moderator",  label: "Moderation",  icon: Gavel,          perm: "venues.moderate", modOnly: true },
  { to: "/admin/events",     label: "Events",      icon: Ticket,         perm: "bookings.view" },
  { to: "/admin/bookings",   label: "Bookings",    icon: CalendarCheck,  perm: "bookings.view" },
  { to: "/admin/check-in",   label: "Check-In",    icon: ScanLine,       perm: "bookings.manage", managerOnly: true },
  { to: "/admin/scan-tickets", label: "Scan Tickets", icon: QrCode,      perm: "bookings.manage", managerOnly: true },
  { to: "/admin/venues",     label: "All Venues",  icon: Building2,      perm: "venues.manage_all", adminOnly: true },
  { to: "/admin/listings",   label: "My Listings", icon: ListChecks,     perm: "venues.manage_assigned" },
  { to: "/admin/rooms",      label: "Room Types",  icon: BedDouble,      perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/calendar",   label: "Calendar",    icon: CalendarDays,   perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/customers",  label: "Customers",   icon: Users,          perm: "users.view", adminOnly: true },
  { to: "/admin/venue-managers", label: "Venue Managers", icon: UserCheck, perm: "users.view", adminOnly: true },
  { to: "/admin/staff", label: "Staff Users", icon: ShieldAlert, perm: "users.manage", adminOnly: true },
  { to: "/admin/pending-payouts", label: "Pending Payouts", icon: Banknote, perm: "bookings.manage", adminOnly: true },
  { to: "/admin/escrows",   label: "Pending Payments", icon: HandCoins,  perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/earnings",   label: "Earnings",    icon: CreditCard,     perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/reviews",    label: "Reviews",     icon: Star,           perm: "venues.manage_assigned", managerOnly: true },
  { to: "/admin/support",    label: "Support",     icon: LifeBuoy,       perm: "bookings.manage", adminOnly: true },
  { to: "/admin/refunds",     label: "Refunds",     icon: RotateCcw,      perm: "bookings.manage", adminOnly: true },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Banknote,       perm: "bookings.manage", adminOnly: true },
];

const ACCOUNT: Item[] = [
  { to: "/admin/wallet",        label: "Wallet",       icon: Wallet },
];

const SYSTEM: Item[] = [
  { to: "/admin/roles",      label: "Role Management", icon: ShieldAlert, perm: "admins.manage", adminOnly: true },
  { to: "/admin/settings",   label: "Settings",    icon: Settings, perm: "settings.manage", adminOnly: true },
];

export function AdminSidebar({
  collapsed, mobileOpen, onMobileClose,
}: { collapsed: boolean; mobileOpen: boolean; onMobileClose: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user, role, hasPermission } = useAuth();
  const isSuperAdmin = role === "super_admin";

  const renderGroup = (label: string, items: Item[]) => {
    const isBusinessOwner = role ? BUSINESS_OWNER_ROLES.includes(role) : false;
    const isModerator = role === "moderator";
    const visible = items.filter((i) => {
      if (i.adminOnly && (isBusinessOwner || isModerator)) return false;
      if (i.managerOnly && !isBusinessOwner) return false;
      if (i.modOnly && !isModerator && role !== "super_admin") return false;
      return !i.perm || hasPermission(i.perm);
    });
    if (!visible.length) return null;
    return (
      <div className="mt-6 first:mt-2">
        {!collapsed && (
          <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        )}
        <nav className="flex flex-col gap-0.5">
          {visible.map((item) => {
            const active = pathname === item.to || (item.to !== "/admin/dashboard" && pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onMobileClose}
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground/70 hover:bg-accent hover:text-foreground",
                )}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="size-5 shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </div>
    );
  };

  const isBusinessOwner = role ? BUSINESS_OWNER_ROLES.includes(role) : false;

  const aside = (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-border/60 bg-card transition-[width] duration-300",
        collapsed ? "w-[72px]" : "w-72",
      )}
    >
      <div className="relative flex h-20 items-center border-b border-border/60">
        <Link
          to="/"
          className={cn(
            "flex items-center overflow-hidden transition-all duration-300 ease-in-out",
            collapsed ? "pl-3" : "pl-[124px]"
          )}
        >
          <img src={favicon} alt="OyoBooking" className="h-10 w-10 shrink-0" />
        </Link>
        {!collapsed && (
          <button onClick={onMobileClose} className="absolute right-4 grid size-8 place-items-center rounded-md hover:bg-accent lg:hidden" aria-label="Close sidebar" title="Close sidebar">
            <X className="size-4" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4">
        {renderGroup("Workspace", PRIMARY)}
        {isBusinessOwner && renderGroup("My Account", ACCOUNT)}
        {renderGroup("System", SYSTEM)}
      </div>


    </aside>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block">{aside}</div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          mobileOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity",
            mobileOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={onMobileClose}
        />
        <div
          className={cn(
            "absolute inset-y-0 left-0 w-72 transition-transform duration-300",
            mobileOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {aside}
        </div>
      </div>
    </>
  );
}
